import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Profession } from '../../../models/profession';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-profession-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './profession-list.component.html',
  styleUrl: './profession-list.component.scss'
})
export class ProfessionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  professions = signal<Profession[]>([]);
  allProfessions = signal<Profession[]>([]);
  isSearchVisible = false;
  rightItem: Profession | undefined = undefined;
  matchedItems: Profession[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadProfessions();
  }

  loadProfessions(): void {
    this.spinnerService.show();
    this.lutService.getProfessions().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortProfessions(data);
        this.allProfessions.set(sortedData);
        this.professions.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading professions:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortProfessions(professions: Profession[]): Profession[] {
    return [...professions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateProfession(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(profession: Profession): void {
  }

  onEditProfession(profession: Profession): void {
  }

  onDeleteProfession(profession: Profession): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(profession.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalProfessions = this.professions();
          const originalAllProfessions = this.allProfessions();

          // 1. Optimistically remove from both local states immediately
          this.professions.update(professions => professions.filter(p => p.id !== profession.id));
          this.allProfessions.update(professions => professions.filter(p => p.id !== profession.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteProfession(profession.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Profession deleted successfully');
              // 3. Update cache so other components get updated data
              this.lookupDataService.updateTableData('professions', this.allProfessions());
            },
            error: (error) => {
              console.error('Error deleting profession:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.professions.set(originalProfessions);
              this.allProfessions.set(originalAllProfessions);
              this.toast.error(`<strong>Failed to delete Profession:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Profession, newName: string} | {item: Profession, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: Profession;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update both local states immediately and re-sort
    this.professions.update(professions => {
      const updated = professions.map(p => p.id === event.item.id ? updatedItem : p);
      return this.sortProfessions(updated);
    });
    this.allProfessions.update(professions => {
      const updated = professions.map(p => p.id === event.item.id ? updatedItem : p);
      return this.sortProfessions(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateProfession(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Profession updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.professions.update(professions => {
          const updated = professions.map(p => p.id === event.item.id ? response : p);
          return this.sortProfessions(updated);
        });
        this.allProfessions.update(professions => {
          const updated = professions.map(p => p.id === event.item.id ? response : p);
          return this.sortProfessions(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('professions', this.allProfessions());
      },
      error: (error) => {
        console.error('Error updating profession:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.professions.update(professions =>
          professions.map(p => p.id === event.item.id ? originalItem : p)
        );
        this.allProfessions.update(professions =>
          professions.map(p => p.id === event.item.id ? originalItem : p)
        );
        this.toast.error(`<strong>Failed to update Profession:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newProfession: Profession = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to both local states immediately and sort
    this.professions.update(professions => this.sortProfessions([...professions, newProfession]));
    this.allProfessions.update(professions => this.sortProfessions([...professions, newProfession]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createProfession({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Profession created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.professions.update(professions => {
          const updated = professions.map(p => p.id === tempId ? response : p);
          return this.sortProfessions(updated);
        });
        this.allProfessions.update(professions => {
          const updated = professions.map(p => p.id === tempId ? response : p);
          return this.sortProfessions(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('professions', this.allProfessions());
      },
      error: (error) => {
        console.error('Error creating profession:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.professions.update(professions =>
          professions.filter(p => p.id !== tempId)
        );
        this.allProfessions.update(professions =>
          professions.filter(p => p.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Profession:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: Profession, matchedItems?: Profession[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onProfessionCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original lists for rollback in case of error
        const originalProfessions = this.professions();
        const originalAllProfessions = this.allProfessions();

        // 1. Optimistically remove merged items from both local states
        this.professions.update(professions =>
          professions.filter(p => !bulkMergeRequest.leftIds.includes(p.id))
        );
        this.allProfessions.update(professions =>
          professions.filter(p => !bulkMergeRequest.leftIds.includes(p.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeProfessionsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Professions merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadProfessions();
          },
          error: (error) => {
            console.error('Error merging professions:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original lists
            this.professions.set(originalProfessions);
            this.allProfessions.set(originalAllProfessions);
            // Restore selection state for retry
            this.rightItem = originalProfessions.find(p => p.id === bulkMergeRequest.rightId);
            this.matchedItems = originalProfessions.filter(p => bulkMergeRequest.leftIds.includes(p.id));
            this.toast.error(`<strong>Failed to merge Professions:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    // Filter from the complete list of professions
    const allProfessions = this.allProfessions();

    if (searchFilter.name && searchFilter.name.trim()) {
      const filteredProfessions = allProfessions.filter(p =>
        p.name.toLowerCase().includes(searchFilter.name!.toLowerCase())
      );
      this.professions.set(filteredProfessions);
    } else {
      // If no search filter, show all professions
      this.professions.set(allProfessions);
    }
  }
}
